import React, { useState, useEffect, useRef } from 'react'
import QRCode from 'react-qr-code'
import TrialRemindersSettings from './TrialRemindersSettings'
import { getPostFooterText, updatePostFooterText, getRegisterWelcome, updateRegisterWelcome, getReferralSidebarSetting, updateReferralSidebarSetting, getReferralPromoSetting, updateReferralPromoSetting, getLoggedInThresholdSetting, updateLoggedInThresholdSetting, getAutomationSetting, updateAutomationSetting, getImageWatermarkSetting, updateImageWatermarkSetting, ImageWatermarkSetting, getProfileQrLoginSetting, updateProfileQrLoginSetting, getProfileLoginSessionSetting, updateProfileLoginSessionSetting, getTutorialSkipSetting, updateTutorialSkipSetting, getPostWaLinkSetting, updatePostWaLinkSetting, getPostStrengthSetting, updatePostStrengthSetting, getLoginWhatsappState, LoginWhatsappState } from '../accounts/core/_requests'

const DEFAULT_WATERMARK: ImageWatermarkSetting = {
  enabled: false,
  imageUrl: 'https://fbzipper.com/assets/d-code-logo-dark-1d926488.svg',
  text: 'פורסם באמצעות FBZipper',
  anchor: 'bottom-right',
  offsetXPercent: 3,
  offsetYPercent: 3,
  logoSizePercent: 18,
  textSizePercent: 4,
  opacity: 90,
  textColor: '#ffffff',
}

// 3x3 anchor grid (rows top→bottom, cols left→right) used by the position picker.
const WATERMARK_ANCHORS: ImageWatermarkSetting['anchor'][] = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'middle-center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
]

// CSS to place the preview watermark block at the given anchor + offsets,
// mirroring the server-side math (centered axis ignores its offset).
const anchorPreviewStyle = (anchor: string, offX: number, offY: number): React.CSSProperties => {
  const [v, h] = (anchor || 'bottom-right').split('-')
  const s: React.CSSProperties = {}
  let tx = '0', ty = '0'
  if (h === 'left') s.left = `${offX}%`
  else if (h === 'right') s.right = `${offX}%`
  else { s.left = '50%'; tx = '-50%' }
  if (v === 'top') s.top = `${offY}%`
  else if (v === 'bottom') s.bottom = `${offY}%`
  else { s.top = '50%'; ty = '-50%' }
  if (tx !== '0' || ty !== '0') s.transform = `translate(${tx}, ${ty})`
  return s
}

const AdminSettingsPage: React.FC = () => {
  const [footerText, setFooterText] = useState('')
  // Welcome-on-register WhatsApp message (global, admin-editable).
  const [welcomeText, setWelcomeText] = useState('')
  const [welcomeSaving, setWelcomeSaving] = useState(false)
  const [welcomeSaved, setWelcomeSaved] = useState(false)
  const [welcomeIsDefault, setWelcomeIsDefault] = useState(true)
  const [referralEnabled, setReferralEnabled] = useState(false)
  const [loggedInThresholdHours, setLoggedInThresholdHours] = useState<number>(24)
  const [loggedInThresholdEnabled, setLoggedInThresholdEnabled] = useState<boolean>(false)
  const [automationEnabled, setAutomationEnabled] = useState<boolean>(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [togglingReferral, setTogglingReferral] = useState(false)
  const [referralPromoEnabled, setReferralPromoEnabled] = useState(false)
  const [togglingReferralPromo, setTogglingReferralPromo] = useState(false)
  // "Skip tutorial" button on every onboarding-tutorial step (customer-facing).
  const [tutorialSkipEnabled, setTutorialSkipEnabled] = useState(false)
  const [postWaLinkEnabled, setPostWaLinkEnabled] = useState(false)
  const [togglingPostWaLink, setTogglingPostWaLink] = useState(false)
  useEffect(() => {
    getPostWaLinkSetting().then((r) => setPostWaLinkEnabled(r.data.enabled)).catch(() => {})
  }, [])
  const [postStrengthEnabled, setPostStrengthEnabled] = useState(false)
  const [togglingPostStrength, setTogglingPostStrength] = useState(false)
  useEffect(() => {
    getPostStrengthSetting().then((r) => setPostStrengthEnabled(r.data.enabled)).catch(() => {})
  }, [])
  const handleTogglePostStrength = async () => {
    setTogglingPostStrength(true)
    try {
      const res = await updatePostStrengthSetting(!postStrengthEnabled)
      setPostStrengthEnabled(res.data.enabled)
    } catch (e) {
      alert('שגיאה בעדכון')
    } finally {
      setTogglingPostStrength(false)
    }
  }
  const handleTogglePostWaLink = async () => {
    setTogglingPostWaLink(true)
    try {
      const res = await updatePostWaLinkSetting(!postWaLinkEnabled)
      setPostWaLinkEnabled(res.data.enabled)
    } catch (e) {
      alert('שגיאה בעדכון')
    } finally {
      setTogglingPostWaLink(false)
    }
  }
  const [togglingTutorialSkip, setTogglingTutorialSkip] = useState(false)
  const [savingThreshold, setSavingThreshold] = useState(false)
  const [thresholdSaved, setThresholdSaved] = useState(false)
  const [togglingThreshold, setTogglingThreshold] = useState(false)
  const [togglingAutomation, setTogglingAutomation] = useState(false)

  // Facebook profile login method. Off (default) = username/password everywhere;
  // on = the QR scan flow, for the day FB's QR login works again.
  const [profileQrLoginEnabled, setProfileQrLoginEnabled] = useState(false)
  const [togglingProfileQrLogin, setTogglingProfileQrLogin] = useState(false)
  // Live streamed login session on /c/:code. Off = credentials form only.
  const [loginSessionEnabled, setLoginSessionEnabled] = useState(false)
  const [togglingLoginSession, setTogglingLoginSession] = useState(false)

  // Image watermark
  const [watermark, setWatermark] = useState<ImageWatermarkSetting>(DEFAULT_WATERMARK)
  const [savingWatermark, setSavingWatermark] = useState(false)
  const [watermarkSaved, setWatermarkSaved] = useState(false)
  const [togglingWatermark, setTogglingWatermark] = useState(false)
  const [sampleImage, setSampleImage] = useState<string>('')
  const [previewWidth, setPreviewWidth] = useState<number>(0)
  const previewRef = useRef<HTMLDivElement>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const sampleInputRef = useRef<HTMLInputElement>(null)

  // WhatsApp login-sender (Baileys) connection state — polled live.
  const [waState, setWaState] = useState<LoginWhatsappState>({ status: 'unknown', qr: '', number: '' })

  useEffect(() => {
    let cancelled = false
    let timer: any = null
    const poll = async () => {
      try {
        const res = await getLoginWhatsappState()
        if (!cancelled) setWaState(res.data)
      } catch (e) { /* keep last state */ }
      if (!cancelled) timer = setTimeout(poll, 3000)
    }
    poll()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [])

  // Track preview width so logo/text sizes can be derived from image-width percentages
  useEffect(() => {
    const el = previewRef.current
    if (!el) return
    const update = () => setPreviewWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [loading])

  const loadSettings = async () => {
    try {
      const [footerRes, welcomeRes, referralRes, promoRes, thresholdRes, automationRes, watermarkRes, qrLoginRes, loginSessionRes, tutorialSkipRes] = await Promise.all([
        getPostFooterText(),
        getRegisterWelcome(),
        getReferralSidebarSetting(),
        getReferralPromoSetting(),
        getLoggedInThresholdSetting(),
        getAutomationSetting(),
        getImageWatermarkSetting(),
        getProfileQrLoginSetting(),
        getProfileLoginSessionSetting(),
        getTutorialSkipSetting(),
      ])
      setFooterText(footerRes.data.post_footer_text)
      setWelcomeText(welcomeRes.data.register_welcome_text)
      setWelcomeIsDefault(welcomeRes.data.is_default)
      setReferralEnabled(referralRes.data.enabled)
      setReferralPromoEnabled(promoRes.data.enabled)
      setLoggedInThresholdHours(thresholdRes.data.hours)
      setLoggedInThresholdEnabled(thresholdRes.data.enabled)
      setAutomationEnabled(automationRes.data.enabled)
      setWatermark({ ...DEFAULT_WATERMARK, ...watermarkRes.data })
      setProfileQrLoginEnabled(qrLoginRes.data.enabled)
      setLoginSessionEnabled(loginSessionRes.data.enabled)
      setTutorialSkipEnabled(tutorialSkipRes.data.enabled)
    } catch (e) {
      console.error('Failed to load settings:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleProfileQrLogin = async () => {
    const next = !profileQrLoginEnabled
    setTogglingProfileQrLogin(true)
    try {
      const res = await updateProfileQrLoginSetting(next)
      setProfileQrLoginEnabled(res.data.enabled)
    } catch (e) {
      console.error('Failed to toggle profile QR login:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingProfileQrLogin(false)
    }
  }

  const handleToggleLoginSession = async () => {
    const next = !loginSessionEnabled
    setTogglingLoginSession(true)
    try {
      const res = await updateProfileLoginSessionSetting(next)
      setLoginSessionEnabled(res.data.enabled)
    } catch (e) {
      console.error('Failed to toggle login session:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingLoginSession(false)
    }
  }

  const patchWatermark = (patch: Partial<ImageWatermarkSetting>) =>
    setWatermark((prev) => ({ ...prev, ...patch }))

  const handleSaveWatermark = async () => {
    setSavingWatermark(true)
    setWatermarkSaved(false)
    try {
      const res = await updateImageWatermarkSetting(watermark)
      const { success, ...data } = res.data
      setWatermark({ ...DEFAULT_WATERMARK, ...data })
      setWatermarkSaved(true)
      setTimeout(() => setWatermarkSaved(false), 3000)
    } catch (e) {
      console.error('Failed to save watermark:', e)
      alert('שגיאה בשמירה')
    } finally {
      setSavingWatermark(false)
    }
  }

  const handleToggleWatermark = async () => {
    const next = !watermark.enabled
    setTogglingWatermark(true)
    try {
      const res = await updateImageWatermarkSetting({ ...watermark, enabled: next })
      const { success, ...data } = res.data
      setWatermark({ ...DEFAULT_WATERMARK, ...data })
    } catch (e) {
      console.error('Failed to toggle watermark:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingWatermark(false)
    }
  }

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 3 * 1024 * 1024) {
      alert('הלוגו גדול מדי (מקסימום 3MB)')
      return
    }
    try {
      const dataUrl = await readFileAsDataUrl(file)
      patchWatermark({ imageUrl: dataUrl })
    } catch {
      alert('שגיאה בטעינת הקובץ')
    }
  }

  const handleSampleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setSampleImage(await readFileAsDataUrl(file))
    } catch {
      alert('שגיאה בטעינת הקובץ')
    }
  }

  const handleSaveThreshold = async () => {
    setSavingThreshold(true)
    setThresholdSaved(false)
    try {
      const hours = Number(loggedInThresholdHours)
      if (!Number.isFinite(hours) || hours < 0) {
        alert('יש להזין מספר שעות תקין')
        return
      }
      await updateLoggedInThresholdSetting({ hours })
      setThresholdSaved(true)
      setTimeout(() => setThresholdSaved(false), 3000)
    } catch (e) {
      console.error('Failed to save threshold:', e)
      alert('שגיאה בשמירה')
    } finally {
      setSavingThreshold(false)
    }
  }

  const handleToggleThreshold = async () => {
    setTogglingThreshold(true)
    try {
      const res = await updateLoggedInThresholdSetting({ enabled: !loggedInThresholdEnabled })
      if (typeof res.data.enabled === 'boolean') {
        setLoggedInThresholdEnabled(res.data.enabled)
      }
    } catch (e) {
      console.error('Failed to toggle threshold:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingThreshold(false)
    }
  }

  const handleToggleAutomation = async () => {
    const next = !automationEnabled
    if (!next && !window.confirm('האם לכבות את כל האוטומציה? פעולות תזמון ופרסום אוטומטיות יחסמו עד להפעלה מחדש.')) {
      return
    }
    setTogglingAutomation(true)
    try {
      const res = await updateAutomationSetting(next)
      setAutomationEnabled(res.data.enabled)
    } catch (e) {
      console.error('Failed to toggle automation:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingAutomation(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    try {
      await updatePostFooterText(footerText)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      console.error('Failed to save:', e)
      alert('שגיאה בשמירה')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveWelcome = async () => {
    setWelcomeSaving(true)
    setWelcomeSaved(false)
    try {
      const res = await updateRegisterWelcome(welcomeText)
      setWelcomeText(res.data.register_welcome_text)
      setWelcomeIsDefault(res.data.is_default)
      setWelcomeSaved(true)
      setTimeout(() => setWelcomeSaved(false), 3000)
    } catch (e) {
      console.error('Failed to save welcome message:', e)
      alert('שגיאה בשמירה')
    } finally {
      setWelcomeSaving(false)
    }
  }

  const handleResetWelcome = async () => {
    if (!window.confirm('לשחזר את הודעת ברוכים הבאים לברירת המחדל?')) return
    setWelcomeSaving(true)
    setWelcomeSaved(false)
    try {
      const res = await updateRegisterWelcome('')
      setWelcomeText(res.data.register_welcome_text)
      setWelcomeIsDefault(res.data.is_default)
      setWelcomeSaved(true)
      setTimeout(() => setWelcomeSaved(false), 3000)
    } catch (e) {
      console.error('Failed to reset welcome message:', e)
      alert('שגיאה בשחזור')
    } finally {
      setWelcomeSaving(false)
    }
  }

  const handleToggleTutorialSkip = async () => {
    setTogglingTutorialSkip(true)
    try {
      const res = await updateTutorialSkipSetting(!tutorialSkipEnabled)
      setTutorialSkipEnabled(res.data.enabled)
    } catch (e) {
      console.error('Failed to toggle tutorial skip:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingTutorialSkip(false)
    }
  }

  const handleToggleReferralPromo = async () => {
    setTogglingReferralPromo(true)
    try {
      const res = await updateReferralPromoSetting(!referralPromoEnabled)
      setReferralPromoEnabled(res.data.enabled)
    } catch (e) {
      console.error('Failed to toggle referral promo:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingReferralPromo(false)
    }
  }

  const handleToggleReferral = async () => {
    setTogglingReferral(true)
    try {
      const res = await updateReferralSidebarSetting(!referralEnabled)
      setReferralEnabled(res.data.enabled)
    } catch (e) {
      console.error('Failed to toggle referral:', e)
      alert('שגיאה בעדכון')
    } finally {
      setTogglingReferral(false)
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>טוען...</div>
    )
  }

  return (
    <div style={{ padding: '24px', direction: 'rtl', maxWidth: '800px' }}>
      <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1f2937', marginBottom: '24px' }}>
        <i className="bi bi-gear" style={{ marginLeft: '8px' }}></i>
        הגדרות מערכת
      </h2>

      {/* Trial-expiry payment reminder messages (authoring only) */}
      <TrialRemindersSettings />

      {/* WhatsApp login-link sender (Baileys) connection */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-whatsapp" style={{ fontSize: '20px', color: '#25D366' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                וואטסאפ לשליחת קישורי התחברות
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                המספר שממנו נשלחים קישורי ההתחברות ללקוחות. סרקו את הקוד פעם אחת כדי לחבר את המספר.
              </p>
            </div>
          </div>
          <span style={{
            fontSize: '12px',
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: '999px',
            background: waState.status === 'connected' ? '#dcfce7' : waState.status === 'qr' ? '#fef9c3' : '#f3f4f6',
            color: waState.status === 'connected' ? '#16a34a' : waState.status === 'qr' ? '#a16207' : '#6b7280',
            whiteSpace: 'nowrap',
          }}>
            {waState.status === 'connected' ? '● מחובר'
              : waState.status === 'qr' ? '○ ממתין לסריקה'
              : waState.status === 'logged_out' ? '○ מנותק'
              : '○ מתחבר...'}
          </span>
        </div>

        <div style={{ padding: '20px', textAlign: 'center' }}>
          {waState.status === 'connected' ? (
            <div style={{ color: '#16a34a', fontSize: '14px', fontWeight: 600 }}>
              <i className="bi bi-check-circle-fill" style={{ marginLeft: '6px' }}></i>
              המספר מחובר{waState.number ? ` (${waState.number})` : ''} ומוכן לשליחה
            </div>
          ) : waState.status === 'qr' && waState.qr ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <div style={{ background: '#fff', padding: '16px', borderRadius: '14px', border: '1px solid #e5e7eb' }}>
                <QRCode value={waState.qr} size={200} fgColor="#1c1e21" bgColor="#ffffff" />
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', maxWidth: '340px', lineHeight: 1.7 }}>
                בטלפון: <b>וואטסאפ</b> ← <b>הגדרות</b> ← <b>מכשירים מקושרים</b> ← <b>קישור מכשיר</b>, וסרקו את הקוד.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '12px' }}>
              <div className="spinner-border" style={{ color: '#25D366', width: 28, height: 28 }}></div>
              <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                {waState.status === 'logged_out' ? 'המספר נותק — ממתין לקוד חדש...' : 'מתחבר לוואטסאפ...'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Global Automation Kill Switch */}
      <div style={{
        background: automationEnabled ? '#fff' : '#fef2f2',
        borderRadius: '12px',
        border: `1px solid ${automationEnabled ? '#e5e7eb' : '#fca5a5'}`,
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className={`bi ${automationEnabled ? 'bi-play-circle-fill' : 'bi-stop-circle-fill'}`} style={{ fontSize: '20px', color: automationEnabled ? '#16a34a' : '#dc2626' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                אוטומציה גלובלית
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: automationEnabled ? '#9ca3af' : '#b91c1c' }}>
                {automationEnabled
                  ? 'אוטומציה דולקת לכל הלקוחות - תזמון ופרסום פעילים.'
                  : 'אוטומציה כבויה - אין תזמון אוטומטי, אין פרסום מיידי, אין הצטרפות מיידית לקבוצות.'}
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingAutomation && handleToggleAutomation()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: automationEnabled
                ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingAutomation ? 'not-allowed' : 'pointer',
              opacity: togglingAutomation ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: automationEnabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* Referral Sidebar Section */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-gift" style={{ fontSize: '18px', color: '#f59e0b' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                תוכנית הפניות (הזמן חבר)
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                הצג את כפתור "הזמן חבר וקבל חודש חינם" בסיידבר לכל הלקוחות (מוצג כברירת מחדל)
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingReferral && handleToggleReferral()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: referralEnabled
                ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingReferral ? 'not-allowed' : 'pointer',
              opacity: togglingReferral ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: referralEnabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* Referral Promo Popup Section */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-megaphone" style={{ fontSize: '18px', color: '#f59e0b' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                פופאפ תוכנית הפניות
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                הצג פופאפ ללקוחות ששילמו לפחות פעם אחת (פעם ביום ל-3 ימים, ואז פעם בחודש). כבוי כברירת מחדל. לתצוגה מקדימה: הוסיפו ?preview_referral_promo=1 לכתובת.
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingReferralPromo && handleToggleReferralPromo()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: referralPromoEnabled
                ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingReferralPromo ? 'not-allowed' : 'pointer',
              opacity: togglingReferralPromo ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: referralPromoEnabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* "Skip tutorial" button inside the onboarding tutorial */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-skip-forward" style={{ fontSize: '18px', color: '#8b5cf6' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                אפשר ללקוחות לדלג על המדריך
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                מציג כפתור "דלג על המדריך" בכל שלב במדריך ההדרכה. כבוי כברירת מחדל – המדריך הוא מה שמגדיר את החשבון (קמפיין, פוסט, קבוצות, פרופיל), ויציאה באמצע משאירה אותו מוגדר חלקית. לאדמין תמיד יש אפשרות לצאת.
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingTutorialSkip && handleToggleTutorialSkip()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: tutorialSkipEnabled
                ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingTutorialSkip ? 'not-allowed' : 'pointer',
              opacity: togglingTutorialSkip ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: tutorialSkipEnabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* "💬 קישור וואטסאפ" generator in the post editor */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-whatsapp" style={{ fontSize: '18px', color: '#25d366' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                כפתור "קישור וואטסאפ" ביצירת פוסט
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                מחולל קישור הוואטסאפ מתחת לתוכן הפוסט ולתגובה האוטומטית. כבוי כרגע לכל הלקוחות.
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingPostWaLink && handleTogglePostWaLink()}
            style={{
              width: '44px', height: '24px', borderRadius: '12px',
              background: postWaLinkEnabled ? 'linear-gradient(135deg, #25d366 0%, #128c46 100%)' : 'rgba(156, 163, 175, 0.4)',
              position: 'relative', cursor: togglingPostWaLink ? 'not-allowed' : 'pointer',
              opacity: togglingPostWaLink ? 0.6 : 1, transition: 'background 0.2s', flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px', height: '18px', borderRadius: '9px', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute', top: '3px', right: postWaLinkEnabled ? '3px' : '23px', transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* "תדירות פרסום" (post strength) in the post editor */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-bar-chart-steps" style={{ fontSize: '18px', color: '#2b70fa' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                "תדירות פרסום" ביצירת פוסט
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                בחירת 1–5: כמה פעמים הפוסט יופיע בלוח הזמנים ביחס לפוסטים אחרים. מוסתר כרגע לכל הלקוחות (פוסטים שומרים את הערך שכבר נקבע).
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingPostStrength && handleTogglePostStrength()}
            style={{
              width: '44px', height: '24px', borderRadius: '12px',
              background: postStrengthEnabled ? 'linear-gradient(135deg, #2b70fa 0%, #7c5cff 100%)' : 'rgba(156, 163, 175, 0.4)',
              position: 'relative', cursor: togglingPostStrength ? 'not-allowed' : 'pointer',
              opacity: togglingPostStrength ? 0.6 : 1, transition: 'background 0.2s', flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px', height: '18px', borderRadius: '9px', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute', top: '3px', right: postStrengthEnabled ? '3px' : '23px', transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* Facebook profile login method (QR vs username/password) */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-qr-code" style={{ fontSize: '18px', color: '#1877f2' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                התחברות פרופילים בברקוד (QR)
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                כבוי = כל חיבור פרופיל (דשבורד, הרשמה, שותפים והקישורים שנשלחים ללקוח) נעשה עם שם משתמש וסיסמה. הפעילו רק אם התחברות הברקוד של פייסבוק חזרה לעבוד.
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingProfileQrLogin && handleToggleProfileQrLogin()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: profileQrLoginEnabled
                ? 'linear-gradient(135deg, #1877f2 0%, #19AFFF 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingProfileQrLogin ? 'not-allowed' : 'pointer',
              opacity: togglingProfileQrLogin ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: profileQrLoginEnabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* Live login session on /c/:code */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-display" style={{ fontSize: '18px', color: '#7239ea' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                מסך התחברות חי (שידור הדפדפן)
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                כבוי = הלקוח מזין בקישור ההתחברות שם משתמש וסיסמה בלבד ומקבל הודעה שתהליך ההתחברות התחיל. מופעל = אחרי הטופס נפתח שידור חי של הדפדפן שבו הוא משלים את ההתחברות והאימות הדו־שלבי.
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingLoginSession && handleToggleLoginSession()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: loginSessionEnabled
                ? 'linear-gradient(135deg, #7239ea 0%, #9d6bff 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingLoginSession ? 'not-allowed' : 'pointer',
              opacity: togglingLoginSession ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: loginSessionEnabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
      </div>

      {/* Logged-in Threshold Section */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-clock-history" style={{ fontSize: '18px', color: '#f59e0b' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                חלון הצגת "מחובר" ללקוחות
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                כשהאפשרות דולקת, ללקוחות שאינם מנהלים פרופיל יוצג כ"מחובר" אם התחבר בתוך מספר השעות הבא. מנהלים רואים תמיד סטטוס אמיתי.
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingThreshold && handleToggleThreshold()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: loggedInThresholdEnabled
                ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingThreshold ? 'not-allowed' : 'pointer',
              opacity: togglingThreshold ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: loggedInThresholdEnabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        <div style={{ padding: '20px', opacity: loggedInThresholdEnabled ? 1 : 0.5, pointerEvents: loggedInThresholdEnabled ? 'auto' : 'none' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
            שעות
          </label>
          <input
            type="number"
            min={0}
            step={1}
            value={loggedInThresholdHours}
            onChange={(e) => setLoggedInThresholdHours(Number(e.target.value))}
            style={{
              width: '140px',
              padding: '10px 14px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#374151',
              direction: 'ltr',
              textAlign: 'right',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={handleSaveThreshold}
              disabled={savingThreshold}
              style={{
                padding: '10px 24px',
                background: savingThreshold ? '#9ca3af' : '#2b70fa',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: savingThreshold ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {savingThreshold ? 'שומר...' : 'שמור'}
            </button>
            {thresholdSaved && (
              <span style={{ color: '#16a34a', fontSize: '13px', fontWeight: 600 }}>
                <i className="bi bi-check-circle-fill" style={{ marginLeft: '4px' }}></i>
                נשמר בהצלחה
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Post Footer Section */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <i className="bi bi-card-text" style={{ fontSize: '16px', color: '#f59e0b' }}></i>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              פוטר פוסטים
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
              הטקסט שיתווסף בסוף כל פוסט עבור חשבונות שבהם הפוטר מופעל
            </p>
          </div>
        </div>

        <div style={{ padding: '20px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
            תוכן הפוטר
          </label>
          <textarea
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            rows={4}
            style={{
              width: '100%',
              padding: '12px 14px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#374151',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: 1.6,
              direction: 'rtl',
              boxSizing: 'border-box',
            }}
          />

          {/* Preview */}
          <div style={{ marginTop: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
              תצוגה מקדימה
            </label>
            <div style={{
              padding: '14px 16px',
              background: '#fff',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              fontSize: '13px',
              lineHeight: 1.7,
            }}>
              <div style={{ color: '#374151' }}>הפוסט של הלקוח יופיע כאן...</div>
              <div style={{
                marginTop: '10px',
                paddingTop: '10px',
                borderTop: '1px dashed #e5e7eb',
                color: '#2b70fa',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
              }}>
                {footerText}
              </div>
            </div>
          </div>

          {/* Save button */}
          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '10px 24px',
                background: saving ? '#9ca3af' : '#2b70fa',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {saving ? 'שומר...' : 'שמור'}
            </button>
            {saved && (
              <span style={{ color: '#16a34a', fontSize: '13px', fontWeight: 600 }}>
                <i className="bi bi-check-circle-fill" style={{ marginLeft: '4px' }}></i>
                נשמר בהצלחה
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Welcome-on-register WhatsApp message Section */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginTop: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <i className="bi bi-whatsapp" style={{ fontSize: '18px', color: '#22c55e' }}></i>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              הודעת ברוכים הבאים בהרשמה (וואטסאפ)
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
              נשלחת אוטומטית בוואטסאפ מיד עם הרשמת לקוח חדש. תגיות זמינות: {'{first_name}'} {'{email}'} {'{password}'} {'{login_url}'}
            </p>
          </div>
        </div>

        <div style={{ padding: '20px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
            תוכן ההודעה
            {welcomeIsDefault && (
              <span style={{ marginRight: '8px', fontSize: '11px', fontWeight: 500, color: '#9ca3af' }}>
                (ברירת מחדל)
              </span>
            )}
          </label>
          <textarea
            value={welcomeText}
            onChange={(e) => setWelcomeText(e.target.value)}
            rows={14}
            style={{
              width: '100%',
              padding: '12px 14px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#374151',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: 1.6,
              direction: 'rtl',
              boxSizing: 'border-box',
            }}
          />

          {/* Save + reset buttons */}
          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={handleSaveWelcome}
              disabled={welcomeSaving}
              style={{
                padding: '10px 24px',
                background: welcomeSaving ? '#9ca3af' : '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: welcomeSaving ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {welcomeSaving ? 'שומר...' : 'שמור'}
            </button>
            <button
              onClick={handleResetWelcome}
              disabled={welcomeSaving || welcomeIsDefault}
              style={{
                padding: '10px 18px',
                background: 'transparent',
                color: (welcomeSaving || welcomeIsDefault) ? '#c7c7c7' : '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: (welcomeSaving || welcomeIsDefault) ? 'not-allowed' : 'pointer',
              }}
            >
              שחזר ברירת מחדל
            </button>
            {welcomeSaved && (
              <span style={{ color: '#16a34a', fontSize: '13px', fontWeight: 600 }}>
                <i className="bi bi-check-circle-fill" style={{ marginLeft: '4px' }}></i>
                נשמר בהצלחה
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Image Watermark Section */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        marginTop: '20px',
      }}>
        <div style={{
          padding: '16px 20px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-images" style={{ fontSize: '18px', color: '#f59e0b' }}></i>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
                חתימת לוגו על תמונות
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                הוסף לוגו וטקסט בפינה הימנית-תחתונה של תמונות שמתפרסמות לפייסבוק
              </p>
            </div>
          </div>
          <div
            onClick={() => !togglingWatermark && handleToggleWatermark()}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: watermark.enabled
                ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                : 'rgba(156, 163, 175, 0.4)',
              position: 'relative',
              cursor: togglingWatermark ? 'not-allowed' : 'pointer',
              opacity: togglingWatermark ? 0.6 : 1,
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              position: 'absolute',
              top: '3px',
              right: watermark.enabled ? '3px' : '23px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Controls column */}
          <div>
            {/* Logo */}
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
              לוגו
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: '#374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}>
                {watermark.imageUrl
                  ? <img src={watermark.imageUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <i className="bi bi-image" style={{ color: '#9ca3af', fontSize: '22px' }}></i>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                <button
                  onClick={() => logoInputRef.current?.click()}
                  style={{ padding: '6px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer' }}
                >
                  <i className="bi bi-upload" style={{ marginLeft: '6px' }}></i>
                  העלה לוגו
                </button>
                {watermark.imageUrl && (
                  <button
                    onClick={() => patchWatermark({ imageUrl: '' })}
                    style={{ padding: '4px 14px', background: 'transparent', border: 'none', fontSize: '12px', color: '#dc2626', cursor: 'pointer', textAlign: 'right' }}
                  >
                    הסר לוגו
                  </button>
                )}
              </div>
            </div>

            {/* Text */}
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
              טקסט
            </label>
            <input
              type="text"
              value={watermark.text}
              onChange={(e) => patchWatermark({ text: e.target.value })}
              placeholder="פורסם באמצעות FBZipper"
              style={{
                width: '100%',
                padding: '10px 14px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#374151',
                direction: 'rtl',
                boxSizing: 'border-box',
                marginBottom: '20px',
              }}
            />

            {/* Text color */}
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
              צבע טקסט
            </label>
            <input
              type="color"
              value={watermark.textColor}
              onChange={(e) => patchWatermark({ textColor: e.target.value })}
              style={{ width: '48px', height: '34px', padding: 0, border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: '#fff' }}
            />

            {/* Position picker (3x3 anchor grid) */}
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', margin: '18px 0 8px', display: 'block' }}>
              מיקום הלוגו
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 38px)', gap: '4px' }}>
              {WATERMARK_ANCHORS.map((a) => {
                const on = (watermark.anchor || 'bottom-right') === a
                return (
                  <button
                    key={a}
                    type="button"
                    title={a}
                    onClick={() => patchWatermark({ anchor: a })}
                    style={{
                      height: '34px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      padding: 0,
                      border: on ? '2px solid #2b70fa' : '1px solid #d1d5db',
                      background: on ? '#2b70fa' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: on ? '#fff' : '#9ca3af', display: 'block' }} />
                  </button>
                )
              })}
            </div>

            {/* Sliders */}
            {([
              { key: 'offsetXPercent', label: 'מרחק אופקי מהקצה (%)', min: 0, max: 50 },
              { key: 'offsetYPercent', label: 'מרחק אנכי מהקצה (%)', min: 0, max: 50 },
              { key: 'logoSizePercent', label: 'גודל הלוגו (% מרוחב התמונה)', min: 2, max: 60 },
              { key: 'textSizePercent', label: 'גודל הטקסט (% מרוחב התמונה)', min: 1, max: 20 },
              { key: 'opacity', label: 'שקיפות (%)', min: 0, max: 100 },
            ] as const).map(({ key, label, min, max }) => (
              <div key={key} style={{ marginTop: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{label}</label>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#2b70fa', direction: 'ltr' }}>{watermark[key]}%</span>
                </div>
                <input
                  type="range"
                  min={min}
                  max={max}
                  step={1}
                  value={watermark[key]}
                  onChange={(e) => patchWatermark({ [key]: Number(e.target.value) } as Partial<ImageWatermarkSetting>)}
                  style={{ width: '100%', accentColor: '#f59e0b', direction: 'ltr' }}
                />
              </div>
            ))}
          </div>

          {/* Preview column */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>תצוגה מקדימה</label>
              <input ref={sampleInputRef} type="file" accept="image/*" onChange={handleSampleUpload} style={{ display: 'none' }} />
              <button
                onClick={() => sampleInputRef.current?.click()}
                style={{ padding: '4px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', fontWeight: 600, color: '#374151', cursor: 'pointer' }}
              >
                <i className="bi bi-image" style={{ marginLeft: '6px' }}></i>
                תמונת דוגמה
              </button>
            </div>
            <div
              ref={previewRef}
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: '10px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                background: sampleImage
                  ? `center / cover no-repeat url(${sampleImage})`
                  : 'linear-gradient(135deg, #64748b 0%, #334155 60%, #1e293b 100%)',
              }}
            >
              {!sampleImage && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
                  תמונת המוצר תופיע כאן
                </div>
              )}
              {/* Watermark block, positioned at the chosen anchor */}
              <div style={{
                position: 'absolute',
                ...anchorPreviewStyle(watermark.anchor, watermark.offsetXPercent, watermark.offsetYPercent),
                opacity: watermark.opacity / 100,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: `${Math.max(2, previewWidth * 0.008)}px`,
                pointerEvents: 'none',
              }}>
                {watermark.imageUrl && (
                  <img
                    src={watermark.imageUrl}
                    alt="watermark"
                    style={{
                      width: `${(previewWidth * watermark.logoSizePercent) / 100}px`,
                      height: 'auto',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
                    }}
                  />
                )}
                {watermark.text && (
                  <span style={{
                    fontSize: `${(previewWidth * watermark.textSizePercent) / 100}px`,
                    color: watermark.textColor,
                    fontWeight: 700,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    textShadow: '0 1px 3px rgba(0,0,0,0.55)',
                    direction: 'rtl',
                  }}>
                    {watermark.text}
                  </span>
                )}
              </div>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#9ca3af', lineHeight: 1.5 }}>
              המיקום והגדלים מחושבים באחוזים מתוך מידות התמונה, כך שהחתימה תיראה זהה על תמונות בכל גודל.
            </p>
          </div>
        </div>

        {/* Save button */}
        <div style={{ padding: '0 20px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleSaveWatermark}
            disabled={savingWatermark}
            style={{
              padding: '10px 24px',
              background: savingWatermark ? '#9ca3af' : '#2b70fa',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: savingWatermark ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {savingWatermark ? 'שומר...' : 'שמור'}
          </button>
          {watermarkSaved && (
            <span style={{ color: '#16a34a', fontSize: '13px', fontWeight: 600 }}>
              <i className="bi bi-check-circle-fill" style={{ marginLeft: '4px' }}></i>
              נשמר בהצלחה
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminSettingsPage
